import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Component, Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ErrorBox, Loading, Toaster } from "@/components/common";
import { InvitePage } from "@/features/auth/InvitePage";
import { LoginPage } from "@/features/auth/LoginPage";
import { Shell } from "@/features/layout/Shell";
import { useSession } from "@/lib/auth";
import { qk, useMe, useOrgPick } from "@/lib/hooks";
import { queryClient } from "@/lib/query";
import { bus } from "@/lib/realtime";
import { resetLive } from "@/store/robots";
import { canEnter, ROUTES, type AppRoute } from "@/routes";
import { zh } from "@/i18n/zh";

/** 登录状态 ↔ 实时连接与缓存：登出时断开总线、清空缓存与实时状态。 */
function SessionLifecycle() {
  const authed = useSession((s) => s.authed);
  // 令牌受限时拿不到实时 ticket（403），不要反复重连
  const restricted = useSession((s) => s.needsTotpSetup || s.needsPasswordChange);
  const qc = useQueryClient();
  useEffect(() => {
    if (!authed || restricted) {
      bus.stop();
      if (!authed) {
        qc.clear();
        resetLive();
        useOrgPick.getState().set(null);
      }
      return;
    }
    bus.start();
    // 权限变化：重新拉 /me 与机器人（按钮随之变化）
    return bus.on((m) => {
      if (m.t === "permissions_changed") {
        void qc.invalidateQueries({ queryKey: qk.me });
        void qc.invalidateQueries({ queryKey: ["robots"] });
      }
    });
  }, [authed, restricted, qc]);
  return null;
}

/** 渲染错误兜底。发版后旧页面懒加载的分块 404（服务端不再回退 index.html）也落到这里，提示刷新。 */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (!this.state.error) return this.props.children;
    const chunk = /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
      this.state.error.message,
    );
    return (
      <div className="p-10 text-center" role="alert">
        <p className="text-lg font-medium">{chunk ? zh.errors.newVersion : zh.errors.title}</p>
        <button className="mt-4 rounded-md border px-4 py-2 text-sm" onClick={() => location.reload()}>
          {zh.errors.reload}
        </button>
      </div>
    );
  }
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const authed = useSession((s) => s.authed);
  const restricted = useSession((s) => s.needsTotpSetup || s.needsPasswordChange);
  const loc = useLocation();
  if (!authed) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (restricted && loc.pathname !== "/account") return <Navigate to="/account" replace />;
  return <>{children}</>;
}

function Guard({ route }: { route: AppRoute }) {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (me.isError) return <ErrorBox error={me.error} onRetry={() => void me.refetch()} />;
  if (!canEnter(me.data, route)) {
    return (
      <div className="p-10 text-center" data-testid="forbidden">
        <p className="text-lg font-medium">{zh.errors.forbidden}</p>
      </div>
    );
  }
  const El = route.element;
  return <El />;
}

/** 首页：第一个有权限的菜单。 */
function Home() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  const first = ROUTES.find((r) => r.nav && canEnter(me.data, r));
  return <Navigate to={first?.path ?? "/account"} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionLifecycle />
        <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route
              element={
                <RequireAuth>
                  <Shell />
                </RequireAuth>
              }
            >
              <Route index element={<Home />} />
              {ROUTES.map((r) => (
                <Route key={r.path} path={r.path} element={<Guard route={r} />} />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
        </ErrorBoundary>
        <Toaster />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
